import { useState } from "react";
import { Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useWaitlist } from "@/hooks/useWaitlist";
import { useAuth } from "@/hooks/useAuth";

interface ComingSoonProps {
  /** Short feature title, e.g. "SEO Manager" */
  feature: string;
  /** Short marketing description shown above the form */
  description?: string;
  /** Bullet list of capabilities the feature will offer */
  highlights?: string[];
  /** Source slug used to track which waitlist this is */
  source?: string;
}

export default function ComingSoon({
  feature,
  description,
  highlights = [],
  source,
}: ComingSoonProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? "");
  const [notes, setNotes] = useState("");
  const { state, submit } = useWaitlist();

  const submitting = state.status === "submitting";
  const succeeded = state.status === "success" || state.status === "duplicate";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    await submit({
      email,
      notes: notes || `Interested in: ${feature}`,
      interest_area: feature,
      source: source ?? `growth_${feature.toLowerCase().replace(/\s+/g, "_")}`,
    });
  }

  return (
    <div className="mx-auto max-w-2xl py-12 px-4">
      <div className="rounded-2xl border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Coming Soon
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">{feature}</h1>
          </div>
        </div>

        {description && (
          <p className="mt-4 text-muted-foreground">{description}</p>
        )}

        {highlights.length > 0 && (
          <ul className="mt-6 space-y-2">
            {highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 border-t pt-6">
          {succeeded ? (
            <div className="rounded-lg bg-primary/5 p-4 text-sm">
              <p className="font-medium">You're on the list. 🎉</p>
              <p className="mt-1 text-muted-foreground">
                {state.status === "duplicate"
                  ? "You're already signed up for early access — we'll be in touch."
                  : "We'll email you the moment this is ready for early access."}
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-sm font-medium">Get early access when it launches</p>
              <div className="space-y-2">
                <Label htmlFor="waitlist-email">Email</Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waitlist-notes">
                  What are you hoping this will solve? <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="waitlist-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Tell us what you'd use it for..."
                />
              </div>
              {state.status === "error" && (
                <p className="text-sm text-destructive">{state.message}</p>
              )}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Notify me at launch"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}